import React, { useMemo, useState } from 'react';
import { 
    useReactTable, 
    getCoreRowModel, 
    getPaginationRowModel, 
    ColumnDef,
    flexRender,
    PaginationState, 
    Row,
} from '@tanstack/react-table';
import { 
    Divider, 
    Flex, 
    Text, 
    Box,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Button,
    IconButton
} from '@chakra-ui/react';
import { TemplateTableProps } from './Template.types';
import IndeterminateCheckbox from '../../components/ui/checkbox/Indeterminate';
import { BsChevronDown, BsChevronLeft, BsChevronRight } from 'react-icons/bs';

export default function TemplateTable({ columns, data, onRowSelected, rowSelection } : Partial<TemplateTableProps>) { 
    const pageSizeOptions = [5, 10, 20, 25, 50, 100]
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    })
    const tableData = data;

    const pagination = useMemo(() => ({
          pageIndex,
          pageSize,
        }),
        [pageIndex, pageSize]
    )

    const tableColumns = React.useMemo<ColumnDef<any>[]>(
        () => [
            {
                id: 'select',
                accessorKey: '',
                header: ({ table }) => (                    
                  <span>
                    <IndeterminateCheckbox
                      {...{
                        checked: table.getIsAllRowsSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: table.getToggleAllRowsSelectedHandler(),
                      }}
                    />
                  </span>
                ),
                cell: ({ row }) => (
                  <div className="px-1">
                    <span className='flex'>
                      <IndeterminateCheckbox
                        {...{
                          checked: row.getIsSelected(),
                          disabled: !row.getCanSelect(),
                          indeterminate: row.getIsSomeSelected(),
                          onChange: row.getToggleSelectedHandler(),
                        }}
                      />
                    </span>
                  </div>
                )
              },
            ...columns!
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], []);

    const table = useReactTable({ 
            data: tableData!, 
            state: {
                pagination,
                rowSelection,
            },
            columns: tableColumns,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            onPaginationChange: setPagination,
            enableRowSelection: true,
            onRowSelectionChange: onRowSelected,
            // meta: {
            //     updateData: (rowIndex: number, columnId: number, value: any) => {            
            //         setTableData(old =>              
            //         old!.map((row, index) => {
            //           if (index === rowIndex) {                  
            //             return {
            //               ...old![rowIndex],
            //               [columnId]: value,
            //             }
            //           }
            //           return row
            //         })
            //       )
            //     },
            // },
            debugTable: true,
    });

    function handleChangePageSize(size: number) {
        setPagination((prevState) => ({
            ...prevState,
            pageSize: size
        }))
    }

    return (
        <>
            <table>
                <thead>
                    {table.getHeaderGroups().map(headerGroup => (                        
                        <tr key={`key-${headerGroup.id}`}>
                            {headerGroup.headers.map(header => (
                                <th 
                                    key={header.id} 
                                    colSpan={header.colSpan} 
                                    style={{ width: header.id === 'select' ? '5%' : 'fit'}}
                                    className='client-table-th'
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getPaginationRowModel().rows.map((row: Row<{}>) => {
                        return (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => {
                                    return (
                                        <td key={cell.id} style={{ width: cell.column.getSize() }}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
            {/* table divider */}
            <Divider marginTop={'1'} marginBottom={'1'} />
             {/* table pagination actions */}
            <Flex justifyContent={'flex-end'} alignItems={'center'} paddingRight="5">
                <Flex alignItems={'center'}>
                    <Text fontSize={'sm'} className='color--text-normal'>Rows per page:</Text>
                    <Box>
                        <Menu>
                            <MenuButton 
                                as={Button} 
                                rightIcon={<BsChevronDown />}
                                px={4}
                                py={2}
                                fontSize="sm"
                                fontWeight={'normal'}
                                style={{ 
                                    border: 'none', 
                                    outline: 'none', 
                                    background: 'transparent',
                                    color: '#212b36',
                                }}
                                transition='all 0.2s'
                                borderRadius='md'
                                borderWidth='0px'
                                _hover={{ color: 'gray.400' }}
                                _expanded={{ color: 'blue.400' }}
                                _focus={{ boxShadow: 'none' }}
                            >
                                { pageSize }
                            </MenuButton>
                            <MenuList>
                                {pageSizeOptions.map((size) => (
                                    <MenuItem 
                                        key={size}
                                        fontSize={'sm'}
                                        onClick={() => handleChangePageSize(size)}
                                    >
                                        {size}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </Menu>
                    </Box>
                </Flex>
                <Box>
                    <span className="flex items-center gap-1">
                        <Text fontSize={'sm'} className='color--text-normal' marginRight={'1'}>Page</Text>
                        <Text fontSize={'sm'}>
                            {table.getState().pagination.pageIndex + 1} of{' '}
                            {table.getPageCount()}
                        </Text>
                    </span>
                </Box>
                <Box>
                    <IconButton
                        variant='ghost'
                        aria-label='Chevron left icon'
                        _hover={{ color: 'blue.400' }}
                        _expanded={{ color: 'blue.400' }}
                        _focus={{ boxShadow: 'none' }}
                        icon={<BsChevronLeft />}
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    />
                    <IconButton
                        variant='ghost'
                        aria-label='Chevron right icon'
                        _hover={{ color: 'blue.400' }}
                        _expanded={{ color: 'blue.400' }}
                        _focus={{ boxShadow: 'none' }}
                        icon={<BsChevronRight/>}
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    />
                </Box>
            </Flex>
        </>
    )
}